/**
 * Composable for managing document metadata (bureaus, document types, search fields)
 *
 * This composable is shared between:
 * - nmed-doc-search-filter-store
 * - projects-store
 * - saved-documents-store
 *
 * Derives filter options from the document search response rather than
 * fetching from a separate metadata endpoint.
 */

import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';
import { alphabetizeStrings } from '@/utils/array-utils';

/* ---------------------------------------------------------------------------------------------- */
/*                                           TYPES                                                */
/* ---------------------------------------------------------------------------------------------- */

export interface UseDocumentMetadataOptions {
   /**
    * Bureaus from the document search response
    */
   responseBureaus: Ref<string[] | undefined>;

   /**
    * Document types from the document search response
    */
   responseDocTypes: Ref<string[] | undefined>;

   /**
    * Search fields from the document search response
    */
   responseSearchFields: Ref<string[] | undefined>;
}

export interface UseDocumentMetadataReturn {
   /**
    * Computed list of available bureaus (sorted alphabetically)
    */
   bureaus: ComputedRef<string[]>;

   /**
    * Computed list of available document types (sorted alphabetically)
    */
   documentTypes: ComputedRef<string[]>;

   /**
    * Computed list of available search fields
    */
   searchFields: ComputedRef<string[]>;

   /**
    * Whether there are no filter options for the current selection
    */
   hasNoFilterOptions: ComputedRef<boolean>;
}

/* ---------------------------------------------------------------------------------------------- */
/*                                       COMPOSABLE                                               */
/* ---------------------------------------------------------------------------------------------- */

export function useDocumentMetadata(options: UseDocumentMetadataOptions): UseDocumentMetadataReturn {
   const bureaus = computed(() => {
      return [...(options.responseBureaus.value || [])].sort(alphabetizeStrings);
   });

   const documentTypes = computed(() => {
      return [...(options.responseDocTypes.value || [])].sort(alphabetizeStrings);
   });

   const searchFields = computed(() => {
      return options.responseSearchFields.value || [];
   });

   const hasNoFilterOptions = computed(() => {
      return documentTypes.value.length === 0 && searchFields.value.length === 0;
   });

   return {
      bureaus,
      documentTypes,
      searchFields,
      hasNoFilterOptions,
   };
}
